// Applies the PartFinder (GoVedia) selected vehicle to site search as a product tag filter.
// Fitment tags use the format Make-Model-Year, e.g. Ford-F-250-2023.
// Wrapped in a function scope so helper names can't clash with app scripts.
(() => {
  const VEHICLE_TAG_PARAM = 'filter.p.tag';
  const VEHICLE_COOKIES = ['gv_mmy', 'gv_vehicle_json'];

  function getCookie(name) {
    const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
    return match ? match.slice(name.length + 1) : null;
  }

  function parseCookieJSON(name) {
    try {
      let value = getCookie(name);
      if (!value) return null;
      // Values may be URL-encoded (sometimes more than once)
      for (let i = 0; i < 3 && /%[0-9A-F]{2}/i.test(value); i++) value = decodeURIComponent(value);
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }

  function getSelectedVehicle() {
    try {
      const vehicleJSON = parseCookieJSON('gv_vehicle_json');
      const details = vehicleJSON && typeof vehicleJSON.mmy === 'object' ? vehicleJSON.mmy : null;

      const mmy = parseCookieJSON('gv_mmy');
      let tag = mmy && typeof mmy.mmy === 'string' ? mmy.mmy.trim() : '';

      if (!tag && details && details.make && details.model && details.year) {
        tag = [details.make, details.model, details.year].join('-');
      }
      if (!tag) return null;

      return { tag, details };
    } catch (error) {
      return null;
    }
  }

  function getVehicleLabel(tag, details) {
    if (details && details.year && details.make && details.model) {
      return [details.year, details.make, details.model, details.submodel].filter(Boolean).join(' ');
    }

    // Fallback: Make-Model-Year -> "Year Make Model"
    const parts = tag.split('-');
    const year = parts.length > 2 && /^\d{4}$/.test(parts[parts.length - 1]) ? parts.pop() : '';
    const make = parts.shift();
    return [year, make, parts.join('-')].filter(Boolean).join(' ');
  }

  function getSearchPath() {
    return (window.routes && window.routes.search_url) || '/search';
  }

  function isSearchForm(form) {
    if (!form || (form.method || 'get').toLowerCase() !== 'get') return false;
    if (!form.querySelector('input[type="search"][name="q"]')) return false;

    try {
      return new URL(form.action, window.location.origin).pathname === getSearchPath();
    } catch (error) {
      return false;
    }
  }

  function onSearchSubmit(event) {
    // Respect other handlers, e.g. predictive search blocking empty submits or opening a selected result
    if (event.defaultPrevented) return;

    const form = event.target;
    if (!isSearchForm(form)) return;

    const vehicle = getSelectedVehicle();
    if (!vehicle) return;

    const params = new URLSearchParams(new FormData(form));
    const query = (params.get('q') || '').trim();
    if (!query) return;
    if (params.getAll(VEHICLE_TAG_PARAM).includes(vehicle.tag)) return;

    event.preventDefault();
    params.set('q', query);
    params.append(VEHICLE_TAG_PARAM, vehicle.tag);
    window.location.href = `${getSearchPath()}?${params.toString()}`;
  }

  function clearSelectedVehicle() {
    const hostname = window.location.hostname;
    const domains = ['', hostname, `.${hostname.replace(/^www\./, '')}`];

    VEHICLE_COOKIES.forEach((name) => {
      domains.forEach((domain) => {
        document.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
      });
    });

    try {
      // Saved by the vehicle restore script in theme.liquid
      localStorage.removeItem('ncbSelectedVehicle');
    } catch (error) {}
  }

  function getUrlWithoutTag(tag) {
    const params = new URLSearchParams(window.location.search);
    const remainingTags = params.getAll(VEHICLE_TAG_PARAM).filter((value) => value !== tag);
    params.delete(VEHICLE_TAG_PARAM);
    remainingTags.forEach((value) => params.append(VEHICLE_TAG_PARAM, value));
    params.delete('page');
    return `${window.location.pathname}?${params.toString()}`;
  }

  function getActiveVehicleFilter() {
    const activeTags = new URLSearchParams(window.location.search).getAll(VEHICLE_TAG_PARAM);
    if (!activeTags.length) return null;

    const vehicle = getSelectedVehicle();
    if (vehicle && activeTags.includes(vehicle.tag)) return vehicle;

    const fitmentTag = activeTags.find((value) => /-\d{4}$/.test(value));
    return fitmentTag ? { tag: fitmentTag, details: null } : null;
  }

  function renderVehicleNote() {
    const header = document.querySelector('.template-search__header');
    if (!header) return;

    let note = header.querySelector('.vehicle-search-note');
    const vehicle = getActiveVehicleFilter();

    if (!vehicle) {
      if (note) note.remove();
      return;
    }

    if (!note) {
      note = document.createElement('p');
      note.className = 'vehicle-search-note center';
      note.setAttribute('role', 'status');
      const searchWrapper = header.querySelector('.template-search__search');
      searchWrapper ? searchWrapper.after(note) : header.append(note);
    }

    const urlWithoutTag = getUrlWithoutTag(vehicle.tag);
    note.textContent = 'Showing results for: ';

    const label = document.createElement('strong');
    label.textContent = getVehicleLabel(vehicle.tag, vehicle.details);

    const showAllLink = document.createElement('a');
    showAllLink.className = 'link underlined-link';
    showAllLink.href = urlWithoutTag;
    showAllLink.textContent = 'Show all results';

    const clearLink = document.createElement('a');
    clearLink.className = 'link underlined-link';
    clearLink.href = urlWithoutTag;
    clearLink.textContent = 'Clear vehicle';
    clearLink.addEventListener('click', clearSelectedVehicle);

    note.append(label, ' · ', showAllLink, ' · ', clearLink);
  }

  document.addEventListener('submit', onSearchSubmit);

  if (document.querySelector('.template-search__header')) {
    renderVehicleNote();

    // Facet changes update the URL without a reload, so keep the note in sync with the results grid
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      new MutationObserver(renderVehicleNote).observe(productGridContainer, { childList: true });
    }
    window.addEventListener('popstate', renderVehicleNote);
  }
})();
